import {
    Box,
    SortDirection,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { MouseEvent, ReactElement, memo } from "react"
import { useNavigate } from "react-router-dom"

import GenSearchResultsMobile from "./mobile/genSearchResultsMobile"
import {
    ASC,
    DESC,
    DRAFT_AMENDMENT,
    DRAFT_DEVICE,
} from "../../../constants/generalConstants"
import {
    TT_CATEGORY,
    TT_COMPLIANCE_STATUS,
    TT_DATE_PUBLISHED,
    TT_MANUFACTURER,
    TT_MODEL,
    TT_PHASE,
    TT_REGISTERED_CAPACITY,
    TT_SYS_REFERENCE,
    TT_TYPE,
} from "../../../constants/tooltips"
import { useScreenSize } from "../../../hooks/useScreenSize"
import {
    SINGLE_GEN_DEVICE_DRAFT_NAV_LINK,
    SINGLE_GEN_DEVICE_NAV_LINK,
} from "../../../router/routes"
import { ttrUIToApiKey } from "../../../services/middleware/mappers/searchGenDevicesMapper"
import AlertFlag from "../../alert/alertFlag"
import CopyTypography from "../../dataDisplay/typography/copyTypography"
import ZeroSearchResultsMsg from "../../fallback/zeroSearchResultsMsg"
import AppTooltip from "../../helper/appTooltip"
import AppSkeleton from "../../loading/skeletons/appSkeleton"
import StatusChip from "../../pills/status/statusChip"
import AppPaper from "../../surfaces/appPaper"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

export interface IDatabaseSearchResult {
    databaseId: number
    deviceReference: string
    status: string
    datePublished: string
    manufacturer: string
    model: string
    category: string
    type: string
    capacity: string
    phases: number
    hasActiveAlert: boolean
}

interface IHeading {
    title: string
    tooltip: string
    id: string
    center: boolean
    enableSort: boolean
}

const headings: IHeading[] = [
    {
        title: "System Reference",
        id: ttrUIToApiKey.deviceReference,
        tooltip: TT_SYS_REFERENCE,
        center: false,
        enableSort: true,
    },
    {
        title: "Compliance Status",
        id: ttrUIToApiKey.status,
        tooltip: TT_COMPLIANCE_STATUS,
        center: false,
        enableSort: false,
    },
    {
        title: "Date Published",
        id: ttrUIToApiKey.datePublished,
        tooltip: TT_DATE_PUBLISHED,
        center: false,
        enableSort: true,
    },
    {
        title: "Manufacturer",
        id: ttrUIToApiKey.manufacturer,
        tooltip: TT_MANUFACTURER,
        center: false,
        enableSort: true,
    },
    {
        title: "Model",
        id: ttrUIToApiKey.model,
        tooltip: TT_MODEL,
        center: false,
        enableSort: true,
    },
    {
        title: "Category",
        id: ttrUIToApiKey.category,
        tooltip: TT_CATEGORY,
        center: false,
        enableSort: true,
    },
    {
        title: "Type",
        id: ttrUIToApiKey.type,
        tooltip: TT_TYPE,
        center: false,
        enableSort: true,
    },
    {
        title: "Capacity (kW)",
        id: ttrUIToApiKey.capacity,
        tooltip: TT_REGISTERED_CAPACITY,
        center: false,
        enableSort: true,
    },
    {
        title: "Phase",
        id: ttrUIToApiKey.phases,
        tooltip: TT_PHASE,
        center: false,
        enableSort: true,
    },
]

type DatabaseSearchTableProps = {
    searchResults: IDatabaseSearchResult[] | undefined
    orderBy: string
    orderDirection: string
    setOrderBy: (orderBy: string, orderDirection: string) => void
    isLoading: boolean
}

/**
 * Displays database search results table for gen devices
 * @example
 * <GenSearchResults searchResults={searchResults}/>
 */
const GenSearchResults = ({
    searchResults,
    orderBy,
    orderDirection,
    setOrderBy,
    isLoading,
}: DatabaseSearchTableProps): ReactElement => {
    const navigate = useNavigate()
    const { isTabletOrMob } = useScreenSize()
    const handleRequestSort = (
        event_: MouseEvent<unknown>,
        property: keyof IDatabaseSearchResult
    ): void => {
        const isAsc = orderBy === property && orderDirection === ASC
        setOrderBy(property, isAsc ? DESC : ASC)
    }
    const createSortHandler =
        (property: keyof IDatabaseSearchResult) =>
        (event: MouseEvent<unknown>) => {
            handleRequestSort(event, property)
        }
    /**
     * Returns flag denoting if no search results returned
     * @returns {boolean}
     */
    const emptySearch = (): boolean => {
        if (isLoading) {
            return false
        }
        if (Array.isArray(searchResults)) {
            return searchResults.length === 0
        }
        if (!searchResults) {
            return true
        }
        return false
    }

    const isDraftStatus = (status: string): boolean =>
        status === DRAFT_AMENDMENT || status === DRAFT_DEVICE

    if (emptySearch()) {
        return (
            <AppPaper>
                <ZeroSearchResultsMsg />
            </AppPaper>
        )
    }

    return isTabletOrMob ? (
        <GenSearchResultsMobile
            searchResults={searchResults as IDatabaseSearchResult[]}
            isLoading={isLoading}
        />
    ) : (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {headings.map((heading: IHeading) => (
                            <AppTooltip
                                title={heading.tooltip}
                                key={heading.title}
                            >
                                <AppTableCell
                                    sortDirection={
                                        orderBy === heading.id
                                            ? (orderDirection as SortDirection)
                                            : false
                                    }
                                    align={heading.center ? "center" : "left"}
                                    sx={{
                                        paddingTop: 0,
                                        paddingBottom: 1,
                                        cursor: heading.enableSort
                                            ? "pointer"
                                            : null,
                                    }}
                                >
                                    <TableSortLabel
                                        active={orderBy === heading.id}
                                        direction={
                                            orderBy === heading.id
                                                ? (orderDirection as
                                                      | "asc"
                                                      | "desc"
                                                      | undefined)
                                                : (ASC as
                                                      | "asc"
                                                      | "desc"
                                                      | undefined)
                                        }
                                        onClick={createSortHandler(
                                            heading.id as keyof IDatabaseSearchResult
                                        )}
                                        disabled={!heading.enableSort}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            color="primary"
                                        >
                                            {heading.title}
                                        </Typography>
                                        {orderBy === heading.id ? (
                                            <Box
                                                component="span"
                                                sx={visuallyHidden}
                                            >
                                                {orderDirection === DESC
                                                    ? "sorted descending"
                                                    : "sorted ascending"}
                                            </Box>
                                        ) : null}
                                    </TableSortLabel>
                                </AppTableCell>
                            </AppTooltip>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {isLoading
                        ? Array.from(Array(20).keys()).map((v) => (
                              <AppTableContentRow
                                  hover
                                  key={`SearchTableSkeleton${v}`}
                              >
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell align="center">
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell align="center">
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell align="center">
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell align="center">
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell align="center">
                                      <AppSkeleton />
                                  </AppTableCell>
                              </AppTableContentRow>
                          ))
                        : (searchResults as IDatabaseSearchResult[]).map(
                              (result: IDatabaseSearchResult) => (
                                  <AppTableContentRow
                                      key={
                                          result.deviceReference +
                                          result.databaseId +
                                          (isDraftStatus(result.status)
                                              ? "draft"
                                              : "device")
                                      }
                                      hover
                                      onClick={() =>
                                          isDraftStatus(result.status)
                                              ? navigate(
                                                    SINGLE_GEN_DEVICE_DRAFT_NAV_LINK(
                                                        result.deviceReference
                                                    )
                                                )
                                              : navigate(
                                                    SINGLE_GEN_DEVICE_NAV_LINK(
                                                        result.deviceReference
                                                    )
                                                )
                                      }
                                      sx={{ cursor: "pointer" }}
                                  >
                                      <AppTableCell>
                                          <Stack direction="row" height="100%">
                                              <CopyTypography
                                                  text={result.deviceReference}
                                                  typographyProps={{
                                                      sx: {
                                                          wordBreak:
                                                              "break-word",
                                                      },
                                                  }}
                                              />
                                              {result.hasActiveAlert && (
                                                  <AlertFlag />
                                              )}
                                          </Stack>
                                      </AppTableCell>
                                      <AppTableCell align="center">
                                          <StatusChip status={result.status} />
                                      </AppTableCell>
                                      <AppTableCell>
                                          {result.datePublished}
                                      </AppTableCell>
                                      <AppTableCell>
                                          {result.manufacturer}
                                      </AppTableCell>
                                      <AppTableCell>
                                          {result.model}
                                      </AppTableCell>
                                      <AppTableCell>
                                          {result.category}
                                      </AppTableCell>
                                      <AppTableCell>{result.type}</AppTableCell>
                                      <AppTableCell align="center">
                                          {result.capacity}
                                      </AppTableCell>
                                      <AppTableCell align="center">
                                          {result.phases}
                                      </AppTableCell>
                                  </AppTableContentRow>
                              )
                          )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default memo(GenSearchResults)
